/**
 * Progressive PDP enhancement. One native form remains responsible for cart
 * validation, selling plans and Shopify analytics. This file only coordinates
 * product choices, gallery, quantity, offer labels and information navigation.
 * Instances are section scoped and cleaned up in the Shopify Theme Editor.
 */
(() => {
  'use strict';
  if (window.ETReimaginedPDP) return;
  window.ETReimaginedPDP = true;
  const instances = new WeakMap();

  /** Resolve all selected options exactly, never silently choose another size. */
  function resolveVariant(variants, choices) {
    return variants.find(variant => variant.options.every((value, index) => value === choices[index]));
  }
  /** Respect Shopify's quantity rules without treating stock as a quantity rule. */
  function normaliseQuantity(value, min, step, max) {
    const number = Number.isFinite(Number(value)) ? Number(value) : min;
    let result = min + Math.max(0, Math.floor((number - min) / step)) * step;
    if (max != null) result = Math.min(result, min + Math.floor((max - min) / step) * step);
    return Math.max(min, result);
  }
  // Export pure decision functions for meaningful variant/quantity regression tests.
  window.ETReimaginedPDPLogic = { resolveVariant, normaliseQuantity };

  function init(section) {
    if (instances.has(section)) return;
    const configNode = section.querySelector('[data-etr-product-data]');
    let data;
    try { data = JSON.parse(configNode.textContent); } catch (_) { return; }
    const events = new AbortController();
    const on = (element, name, fn) => element?.addEventListener(name, fn, { signal: events.signal });
    const form = section.querySelector('.etr-pdp__form');
    const idInput = form.querySelector('[name="id"]');
    const quantity = form.querySelector('[name="quantity"]');
    const add = form.querySelector('[data-etr-add]');
    const options = section.querySelector('[data-etr-options]');
    const native = section.querySelector('[data-etr-native-variant]');
    const track = section.querySelector('[data-etr-media-track]');
    const media = [...track.querySelectorAll('[data-etr-media]')];
    const thumbnails = section.querySelector('[data-etr-thumbnails]');
    const bar = section.querySelector('[data-etr-mobile-bar]');
    let variant = data.variants.find(item => String(item.id) === idInput.value);
    let scrollFrame = 0;
    let barFrame = 0;

    const reviewSummaries = section.querySelectorAll('.etr-pdp__review');
    function syncReviewSummary() {
      reviewSummaries.forEach(summary => summary.querySelectorAll('.jdgm-prev-badge').forEach(badge => {
        const questions = badge.querySelector('.jdgm-qa-badge');
        const counts = [badge, questions].filter(Boolean).map(node => node.getAttribute('data-number-of-questions')).filter(value => value !== null);
        const label = questions?.querySelector('.jdgm-qa-badge__text')?.textContent.replace(/\s+/g, ' ').trim() || '';
        const emptyQuestions = Boolean(questions) && (counts.length ? counts.every(value => value === '0') : /^no questions$/i.test(label));
        badge.toggleAttribute('data-etr-zero-questions', emptyQuestions);
      }));
    }
    const reviewObserver = new MutationObserver(syncReviewSummary);
    reviewSummaries.forEach(summary => reviewObserver.observe(summary, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-number-of-reviews', 'data-number-of-questions'] }));
    syncReviewSummary();

    // STOQ owns the form, consent and alert delivery. Its documented API accepts
    // this small product payload; read the current variant only when clicked.
    // https://docs.stoqapp.com/frontend/docs/javascript-api/
    const notifyButton = section.querySelector('[data-etr-notify]');
    const restockProduct = { id: data.productId, title: data.productTitle, variants: data.variants.map(({ id, title, options, available }) => ({ id, title, options, available })) };
    let restockEnabled = true;
    let restockTimer = 0;
    function updateNotify() {
      if (notifyButton) notifyButton.hidden = !restockEnabled || !variant || variant.available || typeof window._RestockRocket?.openModal !== 'function';
    }
    // The app embed can load before its API is ready. Retry briefly while the
    // app's floating button remains available; never poll for the page lifetime.
    function awaitRestock(attempts = 40) {
      clearTimeout(restockTimer);
      updateNotify();
      if (!notifyButton || !restockEnabled || typeof window._RestockRocket?.openModal === 'function' || attempts === 0) return;
      restockTimer = setTimeout(() => awaitRestock(attempts - 1), 250);
    }
    on(window, 'stoq:loaded', event => {
      restockEnabled = event.detail?.enabled !== false;
      awaitRestock();
    });
    awaitRestock();
    on(notifyButton, 'click', () => {
      if (!variant || variant.available) return;
      try {
        if (typeof window._RestockRocket?.openModal !== 'function') throw new Error('Restock form not ready');
        window._RestockRocket.openModal(restockProduct, String(idInput.value));
      } catch (_) {
        // Restore the app's floating fallback rather than leave a dead control.
        restockEnabled = false;
        updateNotify();
      }
    });

    /** Clarify the verified Bundler offer without replacing its click target.
     * This exact offer adds two selected variants and continues to checkout.
     * Unknown offers retain their app copy. Observe only the purchase region so
     * late app injection and variant rerenders receive the same presentation.
     */
    const purchase = section.querySelector('.etr-pdp__purchase');
    const offerButtons = new WeakSet();
    function clarifyVolumeOffer() {
      purchase?.querySelectorAll('.bndlr-volume[data-bundle-name="ET | Volume discount 2 for 5%"]').forEach(widget => {
        const row = widget.querySelector('.bndlr-volume-discount[data-quantity="2"]');
        const button = row?.querySelector('.bndlr-volume-saving-text');
        if (!button || !/^(Save 5%!|Buy 2 · Save 5%)$/.test(button.textContent.trim())) return;
        const setText = (selector, text) => {
          const node = widget.querySelector(selector);
          if (node && node.textContent !== text) node.textContent = text;
        };
        setText('.bndlr-volume-title', 'Buy two, save 5%');
        setText('.bndlr-volume-description', 'Adds 2 of your selected option and continues to checkout.');
        setText('.bndlr-volume-main-text', 'Discount applied automatically');
        setText('.bndlr-volume-saving-text', 'Buy 2 · Save 5%');
        widget.classList.add('etr-volume-offer');
        // The app uses a div. Keyboard activation forwards one existing click;
        // Bundler remains the sole owner of cart requests and the discount.
        if (button.tagName === 'DIV' && !offerButtons.has(button)) {
          button.setAttribute('role', 'button');
          button.setAttribute('tabindex', '0');
          button.setAttribute('aria-label', 'Buy 2, save 5%, and continue to checkout');
          on(button, 'keydown', event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (!event.repeat) button.click();
          });
          offerButtons.add(button);
        }
      });
    }
    const offerObserver = new MutationObserver(clarifyVolumeOffer);
    if (purchase) offerObserver.observe(purchase, { childList: true, subtree: true });
    clarifyVolumeOffer();

    /** Only the visible gallery item is marked selected; pause hidden HTML video. */
    function markMedia(id) {
      section.querySelectorAll('[data-etr-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.etrThumbnail === String(id))));
      media.forEach(item => { if (item.dataset.etrMedia !== String(id)) item.querySelector('video')?.pause(); });
    }
    function showMedia(id) {
      const item = media.find(node => node.dataset.etrMedia === String(id));
      if (!item) return;
      track.scrollTo({ left: item.offsetLeft - media[0].offsetLeft, behavior: 'instant' });
      markMedia(id);
    }
    on(thumbnails, 'click', event => {
      const button = event.target.closest('[data-etr-thumbnail]');
      if (button) showMedia(button.dataset.etrThumbnail);
    });
    on(track, 'scroll', () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const index = Math.max(0, Math.min(media.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        if (media[index]) markMedia(media[index].dataset.etrMedia);
      });
    });
    track.classList.add('etr-pdp__media-track--enhanced');
    if (thumbnails) thumbnails.hidden = false;
    const initialMedia = track.querySelector('[data-selected-media]');
    if (initialMedia) showMedia(initialMedia.dataset.etrMedia);

    function display(selector, value) {
      const node = section.querySelector(selector);
      node.textContent = value || '';
      node.hidden = !value;
    }
    function updateQuantity() {
      if (!variant) return;
      quantity.min = variant.min;
      quantity.step = variant.step;
      if (variant.max == null) quantity.removeAttribute('max'); else quantity.max = variant.max;
      quantity.value = normaliseQuantity(quantity.value, variant.min, variant.step, variant.max);
      section.querySelector('[data-etr-quantity="-1"]').disabled = Number(quantity.value) <= variant.min;
      section.querySelector('[data-etr-quantity="1"]').disabled = variant.max != null && Number(quantity.value) >= variant.max;
    }
    /** Apply a variant atomically, then notify the existing app/fulfilment hooks.
     * Money is supplied by Liquid, so currency and compare-at values stay exact.
     */
    function updateVariant(next, notify = true) {
      variant = next;
      add.disabled = !variant?.available;
      updateNotify();
      section.querySelector('[data-etr-add-label]').textContent = !variant ? 'Choose an available combination' : variant.available ? data.addLabel : data.soldOutLabel;
      if (!variant) { idInput.value = ''; return; }
      idInput.value = String(variant.id);
      display('[data-etr-price]', variant.price);
      display('[data-etr-sticky-price]', variant.price);
      display('[data-etr-compare]', variant.compare);
      display('[data-etr-saving]', variant.saving);
      display('[data-etr-unit]', variant.unit);
      updateQuantity();
      if (notify) {
        // Change is the native subscription-app contract; no fabricated analytics.
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        window.ETFulfilment?.render(section, variant);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        if (variant.media) showMedia(variant.media);
      }
    }
    function selectFromRadios() {
      const choices = [];
      options.querySelectorAll('input:checked').forEach(input => {
        choices[Number(input.dataset.etrOption)] = input.value;
        section.querySelector(`[data-etr-option-value="${input.dataset.etrOption}"]`).textContent = input.value;
      });
      // Unavailable combinations remain discoverable, but cannot be purchased.
      options.querySelectorAll('input').forEach(input => {
        const candidate = [...choices]; candidate[Number(input.dataset.etrOption)] = input.value;
        input.classList.toggle('is-unavailable', !resolveVariant(data.variants, candidate)?.available);
      });
      updateVariant(resolveVariant(data.variants, choices));
    }
    on(options, 'change', selectFromRadios);
    if (options) { options.hidden = false; native.hidden = true; }
    // Keep native selection support for theme/app integrations and no-JS fallback.
    on(idInput, 'change', () => {
      const next = data.variants.find(item => String(item.id) === idInput.value);
      if (next && next !== variant) {
        options?.querySelectorAll('input').forEach(input => { input.checked = next.options[Number(input.dataset.etrOption)] === input.value; });
        updateVariant(next, false);
      }
    });
    on(section, 'click', event => {
      const button = event.target.closest('[data-etr-quantity]');
      if (!button || !variant) return;
      quantity.value = Number(quantity.value) + Number(button.dataset.etrQuantity) * variant.step;
      updateQuantity(); quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });
    on(quantity, 'change', updateQuantity);
    updateVariant(variant, false);
    // Native fallback links reload the chosen variant; enhanced submission
    // validates the current selection before Shopify performs its own checks.
    on(form, 'submit', event => {
      if (!variant?.available || !form.checkValidity()) { event.preventDefault(); form.reportValidity(); }
    });

    /** A single throttled scroll listener reveals a navigation-only mobile bar.
     * It never triggers checkout or submits a second product form.
     */
    function updateBar() {
      bar.hidden = window.innerWidth >= 900 || form.getBoundingClientRect().bottom >= 0;
    }
    on(window, 'scroll', () => { cancelAnimationFrame(barFrame); barFrame = requestAnimationFrame(updateBar); });
    on(window, 'resize', updateBar);
    updateBar();
    instances.set(section, () => { events.abort(); clearTimeout(restockTimer); reviewObserver.disconnect(); offerObserver.disconnect(); cancelAnimationFrame(scrollFrame); cancelAnimationFrame(barFrame); });
  }

  /** Open native details when arriving via an ingredient/safety deep link. */
  function openHash() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (target?.matches('.etr-details__item')) target.open = true;
  }
  function boot(scope = document) { scope.querySelectorAll('[data-etr-pdp]').forEach(init); openHash(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  window.addEventListener('hashchange', openHash);
  document.addEventListener('click', event => {
    const link = event.target.closest('.etr-pdp__jump-links a');
    const target = link && document.getElementById(link.hash.slice(1));
    if (target?.matches('details')) target.open = true;
  });
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-etr-pdp]').forEach(section => { instances.get(section)?.(); instances.delete(section); }));
})();
